// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface LabeledTruncatedTextProps {
  /** Label to display before the text (e.g., "Return order note:") */
  label: string;
  /** The full text content to display (will be truncated if too long) */
  text: string;
}

/**
 * Displays a label with text content, truncating with ellipsis when the text
 * overflows. Shows a tooltip with full text only when truncation occurs.
 */
export function LabeledTruncatedText(props: LabeledTruncatedTextProps) {
  const { label, text } = props;
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [text]);

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={0.3}
      sx={{ width: '100%' }}
    >
      <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <ConditionalTooltip
        arrow
        tooltipText={isTruncated ? text : undefined}
        placement="top"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          ref={textRef}
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Typography>
      </ConditionalTooltip>
    </Stack>
  );
}
